// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
import * as yup from 'yup'
import { ERC20Wei } from './conversions'
import { ChainId, yupEthAddrRequired, yupErc20WeiRequired } from './Enums'
import { ArrayTypeEnum, EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export interface FeeAccrualAccountCTO {
  poolAddr: EthAddr
  accountAddr: EthAddr
  feeAccrual: ERC20Wei
  lifetimeAccruedFees: ERC20Wei
}

export interface FeeAccrualCTO {
  poolAddr: EthAddr
  chainId: ChainId
  dayNumber: number
  feeAccrualTimestamp: number
  feeAccrual: ERC20Wei
  lifetimeAccruedFees: ERC20Wei
  feeAccrualTimestampDateString: string
  feeAccrualAccountCTOs: FeeAccrualAccountCTO[]
}

export const FeeAccrualCTOAccountSchema = yup.object<FeeAccrualAccountCTO>().shape({
  poolAddr: yupEthAddrRequired.label('Pool Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The address of the pool',
      stringType: StringTypeEnum.ETH_ADDR,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 1,
    })
  ),
  accountAddr: yupEthAddrRequired.label('Account Address').meta(
    toFieldMetaData({
      propName: 'accountAddr',
      description: 'The address of the account',
      stringType: StringTypeEnum.ETH_ADDR,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 1.1,
    })
  ),

  feeAccrual: yupErc20WeiRequired.label('Fee Accrual').meta(
    toFieldMetaData({
      propName: 'feeAccrual',
      description: 'The fee accrued',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 3,
    })
  ),
  lifetimeAccruedFees: yupErc20WeiRequired.label('Lifetime Accrued Fees').meta(
    toFieldMetaData({
      propName: 'lifetimeAccruedFees',
      description: 'The lifetime accrued fees',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 4,
    })
  ),
})
export const FeeAccrualCTOSchema = yup.object<FeeAccrualCTO>().shape({
  poolAddr: yupEthAddrRequired.label('Pool Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The address of the pool',
      stringType: StringTypeEnum.ETH_ADDR,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 1,
    })
  ),
  chainId: yup
    .mixed<ChainId>()
    .oneOf(Object.values(ChainId) as unknown as ChainId[])
    .defined()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', description: 'ChainId of the transaction' })),
  dayNumber: yup
    .number()
    .required()
    .default(0)
    .label('Day Number')
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'The day number',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  feeAccrualTimestamp: yup
    .number()
    .required()
    .default(0)
    .label('Fee Accrual Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'feeAccrualTimestamp',
        description: 'The timestamp',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  feeAccrualTimestampDateString: yup
    .string()
    .required()
    .label('Fee Accrual Timestamp Date')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'feeAccrualTimestampDateString',

        description: 'Timestamp Date of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
      })
    ),
  feeAccrual: yupErc20WeiRequired.label('Fee Accrual').meta(
    toFieldMetaData({
      propName: 'feeAccrual',
      description: 'The fee accrued',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 3,
    })
  ),
  lifetimeAccruedFees: yupErc20WeiRequired.label('Lifetime Accrued Fees').meta(
    toFieldMetaData({
      propName: 'lifetimeAccruedFees',
      description: 'The lifetime accrued fees',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 4,
    })
  ),
  feeAccrualAccountCTOs: yup
    .array()
    .of(FeeAccrualCTOAccountSchema)
    .required()
    .label('Fee Accrual Account CTOs')
    .default([])
    .meta(
      toFieldMetaData({
        propName: 'feeAccrualAccountCTOs',
        description: 'The fee accrual account CTOs',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
        columnWidth: 350,
        arrayType: ArrayTypeEnum.FEE_ACCRUAL_ACCOUNT,
      })
    ),
})

type FeeAccrualCTOInfer = yup.InferType<typeof FeeAccrualCTOSchema>
const _testFeeAccrualCTOSchema: FeeAccrualCTO = {} as unknown as FeeAccrualCTOInfer
const _testBFeeAccrualCTOSchema: FeeAccrualCTOInfer = {} as unknown as FeeAccrualCTO
type FeeAccrualAccountCTOInfer = yup.InferType<typeof FeeAccrualCTOAccountSchema>
const _FeeAccrualCTOSAccountSchema: FeeAccrualAccountCTO = {} as unknown as FeeAccrualAccountCTOInfer
const _testBFeeAccrualCTOSAccountSchema: FeeAccrualAccountCTOInfer = {} as unknown as FeeAccrualAccountCTO
